import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Trophy, Crown, Award, Zap, DollarSign, TrendingUp, Sparkles, MousePointer, Eye, Target } from "lucide-react";
import { Link } from "react-router-dom";
import AIContentCreatorUpsell from "@/components/AIContentCreatorUpsell";
import PodcastSparkline from "@/components/PodcastSparkline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const PERIODS = [
  { id: "week",  label: "Last 7d" },
  { id: "month", label: "Last 30d" },
  { id: "all",   label: "All Time" },
];

export default function OnlyBestStats() {
  const { getAuthHeader } = useAuth();
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/onlybest/me/stats?period=${period}`, { headers: getAuthHeader() })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, getAuthHeader]);

  if (loading) {
    return <div className="space-y-4" data-testid="onlybest-stats-loading">
      <div className="h-28 bg-white/5 rounded-2xl animate-pulse" />
      <div className="h-48 bg-white/5 rounded-2xl animate-pulse" />
    </div>;
  }

  if (!data || data.has_listing === false) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="onlybest-stats-no-listing">
        <Crown className="w-10 h-10 text-[#FBBF24] mx-auto mb-3" />
        <p className="text-white font-semibold">Create or claim your listing first.</p>
        <p className="text-[#94A3B8] text-sm mt-1">Once approved + OnlyBest is enabled, your stats will appear here.</p>
      </div>
    );
  }

  const { stats, level, badges, ledger, earnings_pending, earnings_paid } = data;
  const nextThreshold = level.next_threshold;
  const progressPct = nextThreshold ? Math.min(100, Math.round((stats.points / nextThreshold) * 100)) : 100;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="onlybest-stats-page">
      {/* Hero / Level card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-[#FBBF24]/30 bg-gradient-to-br from-[#FBBF24]/10 via-[#0F172A] to-[#EC4899]/10 p-6 sm:p-8"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#FBBF24]">Your OnlyBest Level</p>
            <h2 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white mt-1 flex items-center gap-2">
              <span className="text-3xl sm:text-4xl">{level.emoji}</span> {level.name}
            </h2>
          </div>
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                  period === p.id ? "bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white" : "text-[#94A3B8] hover:text-white"
                }`}
                data-testid={`stats-period-${p.id}`}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {/* Level progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-[#94A3B8] mb-1">
            <span>{stats.points.toLocaleString()} points</span>
            <span>{nextThreshold ? `Next level at ${nextThreshold.toLocaleString()}` : "Max level reached"}</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.8 }}
              className="h-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899]"
            />
          </div>
        </div>

        {/* Quick stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox icon={Eye} label="Views" value={stats.views} />
          <StatBox icon={MousePointer} label="Clicks" value={stats.clicks} />
          <StatBox icon={Target} label="Leads" value={stats.conversions} highlight />
          <StatBox icon={Trophy} label="Global Rank" value={stats.rank ? `#${stats.rank}` : "—"} highlight />
        </div>
      </motion.div>

      {/* Podcast plays sparkline */}
      <PodcastSparkline getAuthHeader={getAuthHeader} />

      {/* Badges */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Award className="w-4 h-4 text-[#FBBF24]" /> Badges Earned
        </h2>
        {badges.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No badges yet. Share your tracked links and watch them stack up.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="onlybest-badges-list">
            {badges.map((b) => (
              <div key={b.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FBBF24]/15 to-[#EC4899]/10 border border-[#FBBF24]/30 text-sm" data-testid={`badge-${b.id}`}>
                <span>{b.emoji}</span> <span className="text-white">{b.label}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Commissions / Earnings */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#10B981]" /> Earnings Ledger
          </h2>
          <Link to="/leaderboard" className="text-xs text-[#FBBF24] hover:text-white inline-flex items-center gap-1">
            <Trophy className="w-3.5 h-3.5" /> View leaderboard
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-white/10 bg-[#0F172A] p-4">
            <p className="text-xs text-[#94A3B8]">Pending</p>
            <p className="text-2xl font-bold text-white">${earnings_pending.toFixed(2)}</p>
          </div>
          <div className="rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 p-4">
            <p className="text-xs text-[#10B981]">Paid out</p>
            <p className="text-2xl font-bold text-white">${earnings_paid.toFixed(2)}</p>
          </div>
        </div>
        {ledger.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-6">
            Once approved commissions are recorded, they'll show up here. Stripe Connect payouts coming soon.
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {ledger.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-3 min-w-0" data-testid={`ledger-row-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-white text-sm truncate">{c.business_slug} <span className="text-[#64748B]">· {c.type}</span></p>
                  <p className="text-xs text-[#64748B]">{new Date(c.created_at).toLocaleDateString()} · {c.notes || "—"}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-white font-semibold tabular-nums">${(c.amount || 0).toFixed(2)}</p>
                  <p className={`text-xs ${c.status === "paid" ? "text-[#10B981]" : "text-[#FBBF24]"}`}>{c.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <div className="rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-br from-[#FBBF24]/10 to-transparent p-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-white font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-[#FBBF24]" /> Want to climb faster?</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">Share your tracked links, post fresh picks, and recommend more businesses.</p>
        </div>
        <Link to="/dashboard/onlybest" className="px-4 py-2 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-sm font-semibold">
          Open OnlyBest Campaign
        </Link>
      </div>

      {/* Dashboard-only upsell — AI Content Creator */}
      <AIContentCreatorUpsell />
    </div>
  );
}

function StatBox({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? "border-[#FBBF24]/30 bg-[#FBBF24]/5" : "border-white/10 bg-white/5"}`}>
      <Icon className={`w-4 h-4 ${highlight ? "text-[#FBBF24]" : "text-[#94A3B8]"} mb-1.5`} />
      <p className="text-xl sm:text-2xl font-bold text-white tabular-nums">{value}</p>
      <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{label}</p>
    </div>
  );
}
